import React, { useState, useMemo } from 'react';
import Calendar from 'react-calendar';
import { format, parseISO, isValid, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import 'react-calendar/dist/Calendar.css';
import './ScheduleView.css';

const ScheduleView = ({ placements, filters }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'list'

  // Process placements into calendar events
  const calendarEvents = useMemo(() => {
    const events = [];

    placements.forEach(placement => {
      // Application deadlines (if status is still Applied)
      if (placement.application_date && placement.application_status === 'Applied') {
        events.push({
          id: `${placement.id}-app`,
          date: placement.application_date,
          type: 'application',
          title: `Applied to ${placement.company_name}`,
          company: placement.company_name,
          role: placement.position_role,
          status: placement.application_status,
          priority: placement.priority_rating
        });
      }

      // Online Assessment dates
      if (placement.online_assessment_date) {
        events.push({
          id: `${placement.id}-oa`,
          date: placement.online_assessment_date,
          type: 'assessment',
          title: `OA - ${placement.company_name}`,
          company: placement.company_name,
          role: placement.position_role,
          status: placement.application_status,
          priority: placement.priority_rating
        });
      }

      // Interview dates
      if (placement.interview_dates && placement.interview_dates.length > 0) {
        placement.interview_dates.forEach((date, index) => {
          events.push({
            id: `${placement.id}-int-${index}`,
            date: date,
            type: 'interview',
            title: `Interview ${index + 1} - ${placement.company_name}`,
            company: placement.company_name,
            role: placement.position_role,
            status: placement.application_status,
            priority: placement.priority_rating
          });
        });
      }

      // Result dates (for pending results)
      if (placement.result_date && ['Applied', 'Session', 'Interview Scheduled'].includes(placement.application_status)) {
        events.push({
          id: `${placement.id}-result`,
          date: placement.result_date,
          type: 'result',
          title: `Result Expected - ${placement.company_name}`,
          company: placement.company_name,
          role: placement.position_role,
          status: placement.application_status,
          priority: placement.priority_rating
        });
      }
    });

    return events.sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [placements]);

  // Get events for selected date
  const selectedDateEvents = useMemo(() => {
    return calendarEvents.filter(event => {
      try {
        const eventDate = parseISO(event.date);
        return isValid(eventDate) && isSameDay(eventDate, selectedDate);
      } catch {
        return false;
      }
    });
  }, [calendarEvents, selectedDate]);

  // Get upcoming events (next 7 days)
  const upcomingEvents = useMemo(() => {
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    return calendarEvents.filter(event => {
      try {
        const eventDate = parseISO(event.date);
        return isValid(eventDate) && eventDate >= today && eventDate <= nextWeek;
      } catch {
        return false;
      }
    }).slice(0, 10); // Limit to 10 events
  }, [calendarEvents]);

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'application': return '#007bff';
      case 'assessment': return '#6f42c1';
      case 'interview': return '#ffc107';
      case 'result': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'application': return 'fa-paper-plane';
      case 'assessment': return 'fa-laptop-code';
      case 'interview': return 'fa-users';
      case 'result': return 'fa-flag-checkered';
      default: return 'fa-circle';
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'MMM dd, yyyy') : 'Invalid Date';
    } catch {
      return 'Invalid Date';
    }
  };

  const formatTime = (dateString) => {
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'h:mm a') : '';
    } catch {
      return '';
    }
  };

  // Custom tile content for calendar
  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const dayEvents = calendarEvents.filter(event => {
        try {
          const eventDate = parseISO(event.date);
          return isValid(eventDate) && isSameDay(eventDate, date);
        } catch {
          return false;
        }
      });

      if (dayEvents.length > 0) {
        return (
          <div className="calendar-events">
            {dayEvents.slice(0, 3).map(event => (
              <div
                key={event.id}
                className="calendar-event-dot"
                style={{ backgroundColor: getEventTypeColor(event.type) }}
                title={event.title}
              />
            ))}
            {dayEvents.length > 3 && (
              <div className="calendar-event-more">+{dayEvents.length - 3}</div>
            )}
          </div>
        );
      }
    }
    return null;
  };

  return (
    <div className="schedule-container">
      <div className="schedule-header">
        <h3>Interview Schedule</h3>
        <p>Calendar view of upcoming interviews and deadlines</p>
      </div>

      {/* View Toggle */}
      <div className="schedule-controls">
        <div className="view-toggle">
          <button
            className={`btn ${viewMode === 'month' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode('month')}
          >
            <i className="fas fa-calendar me-2"></i>
            Calendar
          </button>
          <button
            className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode('list')}
          >
            <i className="fas fa-list me-2"></i>
            List
          </button>
        </div>

        <div className="schedule-stats">
          <span className="event-count">{calendarEvents.length} total events</span>
        </div>
      </div>

      <div className="schedule-content">
        {viewMode === 'month' ? (
          <div className="calendar-view">
            <div className="calendar-section">
              <Calendar
                onChange={setSelectedDate}
                value={selectedDate}
                tileContent={tileContent}
                className="custom-calendar"
              />
            </div>

            <div className="events-sidebar">
              <h5>
                Events for {format(selectedDate, 'MMMM dd, yyyy')}
              </h5>

              {selectedDateEvents.length === 0 ? (
                <div className="no-events-day">
                  <i className="fas fa-calendar-day fa-2x text-muted mb-2"></i>
                  <p>No events scheduled</p>
                </div>
              ) : (
                <div className="day-events">
                  {selectedDateEvents.map(event => (
                    <div key={event.id} className="event-card">
                      <div className="event-header">
                        <div
                          className="event-icon"
                          style={{ backgroundColor: getEventTypeColor(event.type) }}
                        >
                          <i className={`fas ${getEventTypeIcon(event.type)}`}></i>
                        </div>
                        <div className="event-info">
                          <h6>{event.title}</h6>
                          <p>{event.role}</p>
                        </div>
                      </div>

                      <div className="event-meta">
                        <span className="event-status">{event.status}</span>
                        {event.priority && (
                          <span className="event-priority">
                            {'★'.repeat(event.priority)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="list-view">
            <div className="upcoming-section">
              <h5>Upcoming Events (Next 7 Days)</h5>

              {upcomingEvents.length === 0 ? (
                <div className="no-upcoming">
                  <i className="fas fa-calendar-check fa-3x text-muted mb-3"></i>
                  <h6>No Upcoming Events</h6>
                  <p className="text-muted">You're all caught up!</p>
                </div>
              ) : (
                <div className="upcoming-events">
                  {upcomingEvents.map(event => (
                    <div key={event.id} className="upcoming-event">
                      <div className="event-date">
                        <div className="date-day">
                          {format(parseISO(event.date), 'dd')}
                        </div>
                        <div className="date-month">
                          {format(parseISO(event.date), 'MMM')}
                        </div>
                      </div>

                      <div className="event-details">
                        <h6>{event.title}</h6>
                        <p>{event.role}</p>
                        <div className="event-badges">
                          <span
                            className="type-badge"
                            style={{ backgroundColor: getEventTypeColor(event.type) }}
                          >
                            {event.type}
                          </span>
                          <span className="status-badge">{event.status}</span>
                        </div>
                      </div>

                      {event.priority && (
                        <div className="event-priority-list">
                          {'★'.repeat(event.priority)}{'☆'.repeat(5 - event.priority)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleView;
